/**
 * PhysicalHabitCard — holistic view of the user's physical habit.
 *
 * Powered by /api/dashboard/physical. Renders score + breakdown,
 * volume/frequency/intensity/consistency/trend, WHO comparison and
 * the natural-language insights generated by `physical_habit.py`.
 */
import { Activity, Sparkles, TrendingUp, TrendingDown, Minus, Repeat, Flame } from "lucide-react";
import { formatScore, calendarBandLabel } from "@/lib/scoring";

const BAND_COLORS = {
  excelente: "#2A9D8F",
  muy_bueno: "#3DB099",
  bueno: "#7AB287",
  adecuado: "#D4B16A",
  bajo: "#C97A60",
  muy_bajo: "#B85C50",
};

const TREND_ICON = {
  improving: TrendingUp,
  starting: Sparkles,
  stable: Minus,
  softly_declining: TrendingDown,
  declining: TrendingDown,
};

const TREND_LABEL = {
  improving: "En mejora",
  starting: "Arrancando",
  stable: "Estable",
  softly_declining: "Bajando suave",
  declining: "En descenso",
};

const WHO_LABEL = {
  insufficient: "Por debajo de las recomendaciones",
  adequate: "Recomendación cumplida",
  very_good: "Por encima del óptimo",
  excellent: "Excelente nivel de actividad",
};

const TONE_COLORS = {
  positive: { bg: "rgba(222, 232, 221, 0.55)", border: "rgba(141,187,142,0.5)", dot: "#2A9D8F" },
  neutral: { bg: "rgba(255, 251, 240, 0.7)", border: "rgba(218, 207, 186, 0.55)", dot: "#7C7A6E" },
  warning: { bg: "rgba(248, 220, 210, 0.45)", border: "rgba(224,122,95,0.4)", dot: "#C97A60" },
};

export default function PhysicalHabitCard({ data }) {
  if (!data) return null;
  const color = BAND_COLORS[data.score_band] || BAND_COLORS.adecuado;
  const TrendIcon = TREND_ICON[data.trend.direction] || Minus;

  const whoPct = data.who.pct_of_recommendation;
  const whoCapped = Math.min(200, Math.max(0, whoPct));

  return (
    <section
      className="rounded-3xl p-6 sm:p-7 border backdrop-blur-md space-y-6"
      style={{
        background:
          "linear-gradient(155deg, rgba(255, 251, 240, 0.78) 0%, rgba(244, 236, 215, 0.45) 100%)",
        borderColor: "rgba(218, 207, 186, 0.5)",
        boxShadow: "0 18px 50px -28px rgba(83,109,83,0.22), 0 1px 0 rgba(255,255,255,0.65) inset",
      }}
      data-testid="physical-habit-card"
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="h-12 w-12 rounded-2xl flex items-center justify-center"
            style={{ background: `${color}24`, color }}
          >
            <Activity size={22} />
          </div>
          <div>
            <p className="tk-label">Hábito físico</p>
            <h2 className="font-outfit text-2xl text-ink-900 mt-0.5">
              {data.score_label}
            </h2>
            {data.baseline_confidence && data.baseline_confidence !== "high" && (
              <BaselineChip
                confidence={data.baseline_confidence}
                days={data.data_maturity_days}
                label={data.baseline_confidence_label}
              />
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="font-outfit text-5xl text-ink-900 leading-none" style={{ color }} data-testid="physical-score">
            {formatScore(data.score)}
          </p>
          <p className="tk-label mt-1">de 10</p>
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric
          icon={<Flame size={14} />}
          label="Carga semanal"
          value={`${data.load.load_last_7d}`}
          sub={`${data.volume.minutes_last_7d} min`}
          tone="strong"
          testId="metric-load"
        />
        <Metric
          icon={<Repeat size={14} />}
          label="Frecuencia"
          value={`${data.frequency.sessions_last_7d}`}
          sub={data.frequency.sessions_last_7d === 1 ? "sesión / semana" : "sesiones / semana"}
          testId="metric-frequency"
        />
        <Metric
          icon={<Activity size={14} />}
          label="Intensidad media"
          value={
            data.intensity.avg_intensity_when_trained != null
              ? data.intensity.avg_intensity_when_trained.toFixed(1)
              : "—"
          }
          sub={data.intensity.band !== "unknown" ? intensityLabel(data.intensity.band) : "Sin datos"}
          testId="metric-intensity"
        />
        <Metric
          icon={<Sparkles size={14} />}
          label="Consistencia"
          value={`${data.consistency.active_weeks_90d}/${data.consistency.total_weeks_90d}`}
          sub={consistencySub(data)}
          testId="metric-consistency"
        />
      </div>

      <ScoreBreakdown breakdown={data.score_breakdown} />

      <WhoBar pct={whoCapped} band={data.who.band} minutes={data.who.weekly_minutes_user} target={data.who.weekly_target_min} optimal={data.who.weekly_target_optimal} />

      <div className="flex items-center gap-2">
        <TrendIcon size={16} className="text-sage-600" />
        <p className="font-dmsans text-sm text-ink-900" data-testid="physical-trend">
          {TREND_LABEL[data.trend.direction] || "—"}
        </p>
        <span className="font-dmsans text-xs text-ink-600">
          {data.trend.weekly_delta_load_pct >= 0 ? "+" : ""}
          {data.trend.weekly_delta_load_pct}% carga vs semana previa
        </span>
      </div>

      {data.personal_baseline.has_baseline && (
        <p className="font-dmsans text-xs text-ink-600" data-testid="physical-baseline">
          Tu patrón habitual: <span className="text-ink-900">
            ~{data.personal_baseline.baseline_weekly_sessions} sesiones/semana, carga {data.personal_baseline.baseline_weekly_load}
          </span>.
        </p>
      )}

      {(data.insights || []).length > 0 && (
        <div className="space-y-2" data-testid="physical-insights">
          <p className="tk-label">Lo que vemos</p>
          {data.insights.map((ins, idx) => {
            const c = TONE_COLORS[ins.tone] || TONE_COLORS.neutral;
            return (
              <div
                key={idx}
                className="rounded-2xl px-4 py-3 flex items-start gap-3 border"
                style={{ background: c.bg, borderColor: c.border }}
                data-testid={`physical-insight-${idx}`}
              >
                <span
                  aria-hidden="true"
                  className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                  style={{ background: c.dot }}
                />
                <p className="font-dmsans text-sm text-ink-900 leading-relaxed">{ins.text}</p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Metric({ icon, label, value, sub, tone, testId }) {
  return (
    <div
      className="rounded-2xl p-3 border"
      style={{
        background: tone === "strong" ? "rgba(222, 232, 221, 0.55)" : "rgba(255, 251, 240, 0.65)",
        borderColor: "rgba(218, 207, 186, 0.55)",
      }}
      data-testid={testId}
    >
      <p className="font-dmsans text-[10px] tracking-[0.16em] uppercase text-ink-600 flex items-center gap-1.5">
        <span className="text-sage-600">{icon}</span>
        {label}
      </p>
      <p className="font-outfit text-2xl text-ink-900 mt-1 leading-none">{value}</p>
      <p className="font-dmsans text-xs text-ink-600 mt-1">{sub}</p>
    </div>
  );
}

function ScoreBreakdown({ breakdown }) {
  const rows = [
    { id: "load", label: "Carga", weight: 0.4 },
    { id: "consistency", label: "Consistencia", weight: 0.3 },
    { id: "frequency", label: "Frecuencia", weight: 0.2 },
    { id: "trend", label: "Tendencia", weight: 0.1 },
  ];
  return (
    <div className="space-y-2" data-testid="physical-breakdown">
      <p className="tk-label">Cómo se compone el score</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {rows.map((r) => {
          const v = breakdown[r.id];
          const pct = ((v - 1) / 9) * 100;
          return (
            <div key={r.id} className="space-y-1" data-testid={`breakdown-${r.id}`}>
              <div className="flex items-baseline justify-between">
                <span className="font-dmsans text-xs text-ink-900">
                  {r.label} · <span className="text-ink-600">{Math.round(r.weight * 100)}%</span>
                </span>
                <span className="font-outfit text-sm text-sage-700">{v.toFixed(1)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-sand-100/80 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-sage-400 to-sage-500"
                  style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WhoBar({ pct, band, minutes, target, optimal }) {
  return (
    <div data-testid="physical-who">
      <div className="flex items-baseline justify-between mb-2">
        <p className="tk-label">Vs recomendación general</p>
        <p className="font-dmsans text-xs text-ink-600">
          {minutes} min — {WHO_LABEL[band]}
        </p>
      </div>
      <div className="relative h-3 rounded-full bg-sand-100/80 overflow-hidden">
        {/* Optimal zone marker (300 min = 200% of 150 min target) */}
        <span
          className="absolute top-0 bottom-0 border-l border-r border-dashed pointer-events-none"
          style={{
            left: `${(target / (optimal * 2)) * 100}%`,
            right: `${100 - (optimal / (optimal * 2)) * 100}%`,
            borderColor: "rgba(141,187,142,0.55)",
          }}
        />
        <div
          className="h-full rounded-full bg-gradient-to-r from-sage-400 to-sage-500 transition-all"
          style={{ width: `${(pct / 2)}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-[10px] font-dmsans text-ink-600/70">
        <span>0 min</span>
        <span>{target} (mín)</span>
        <span>{optimal} (óptimo)</span>
        <span>{optimal * 2}+ min</span>
      </div>
    </div>
  );
}

function intensityLabel(band) {
  return {
    low: "Baja",
    moderate: "Moderada",
    high: "Alta",
    very_high: "Muy alta",
  }[band] || "—";
}

function consistencySub(data) {
  const pct = data.consistency.adherence_pct;
  const weeks = data.consistency.total_weeks_90d;
  if (weeks >= 13) return `${pct}% últimos 90 días`;
  const noun = weeks === 1 ? "semana" : "semanas";
  return `${pct}% (sobre ${weeks} ${noun})`;
}

function BaselineChip({ confidence, days, label }) {
  const styles = confidence === "low"
    ? { bg: "rgba(244, 236, 215, 0.85)", border: "rgba(218, 207, 186, 0.7)", dot: "#A89878" }
    : { bg: "rgba(222, 232, 221, 0.55)", border: "rgba(141,187,142,0.45)", dot: "#7AB287" };
  const compactLabel = confidence === "low"
    ? "Línea base en construcción"
    : "Patrón estabilizándose";
  const detail = typeof days === "number"
    ? `· ${days} ${days === 1 ? "día" : "días"} de historial`
    : "";
  return (
    <div
      className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full border"
      style={{ background: styles.bg, borderColor: styles.border }}
      data-testid="baseline-chip"
      data-confidence={confidence}
      title={label}
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: styles.dot }}
      />
      <span className="font-dmsans text-[11px] tracking-[0.04em] text-ink-700">
        {compactLabel} {detail}
      </span>
    </div>
  );
}
